import { memo, useEffect, useMemo, useState } from "react"
import { Box, LinearProgress, Stack, styled, Icon, IconButton, Tab, TabPanel, Tabs, Tooltip, Typography, Fade } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { PureList } from "@bundles/parts/components/PureList"
import { ArticleListType, CrossSellingModalType, ECrossSellingType } from "@tm/models"
import { ParticularStateProvider } from "@bundles/parts/components/PureList/ContextProviders/Particular/ParticularContext"
import { usePrevious } from "@tm/utils"
import { useArticleListConfiguration } from "../../ArticleListConfiguration"
import { usePartsAlternativesModuleState } from "../../PartsAlternatives/PartsAlternativesModuleState"
import { useListOptionsContext } from "../../ContextProvider"
import { ArticleListFeaturesDisableProvider } from "../../ArticleListConfiguration/ArticleListFeaturesDisable"
import { ProductGroupButton, ArticlesChain } from "./components"
import { useCrossSelling } from "./hooks"
import { CrossSellingTabType } from "./models"
import { isCrossSellingEnabledForList } from "./helpers"
import { crossSellingListId } from "./crossSellingStates"

const Container = styled(Box)({
    backgroundColor: "#fafafa",
    boxShadow: "0 1px 5px rgb(0 0 0 / 20%)",
    padding: "10px 5px 5px 20px",
    marginLeft: 16,
})

type CrossSellingProps = {
    listType: ArticleListType
    newSearchLoaded?: boolean
    crossSellingModalType?: CrossSellingModalType | undefined
}

type CrossSellingComponentProps = CrossSellingProps & {
    isAlternativeSearch?: boolean
}

const CrossSellingComponent = memo(({ listType, isAlternativeSearch, newSearchLoaded, crossSellingModalType }: CrossSellingComponentProps) => {
    const { translateText } = useLocalization()

    const {
        isLoading,
        isEnabled,
        articleOrder,
        data,
        chains,
        displayCustomerAlsoBoughtTab,
        displayCustomerAlsoBoughtButton,
        pureListContext,
        onProductGroupClick,
        onClose,
        onReset,
        preventReset,
        setPreventReset,
    } = useCrossSelling(listType, isAlternativeSearch, crossSellingModalType)

    const displayArticlesChain = useMemo(
        () => (chains.alsoBoughtArticles?.length > 0 ? chains.alsoBoughtArticles : chains.alsoBoughtProductGroups),
        [chains.alsoBoughtArticles, chains.alsoBoughtProductGroups]
    )
    const prevNewSearchLoaded = usePrevious(newSearchLoaded)

    const { showArticleImages, compactView } = useListOptionsContext()

    const [activeNavIdentifier, setActiveNavIdentifier] = useState<CrossSellingTabType>(ECrossSellingType.alsoBoughtArticles)

    useEffect(() => {
        const becameTrue = prevNewSearchLoaded === false && newSearchLoaded
        if (!becameTrue) {
            return
        }

        if (newSearchLoaded) {
            if (preventReset) {
                setPreventReset(false)
            } else {
                onReset()
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newSearchLoaded])

    useEffect(() => {
        setActiveNavIdentifier(displayCustomerAlsoBoughtTab ? ECrossSellingType.alsoBoughtArticles : ECrossSellingType.recommendedProductGroups)
    }, [displayCustomerAlsoBoughtTab])

    if (!isEnabled || !newSearchLoaded) {
        return null
    }

    const handleNavSelect = (_: unknown, nextNavIdentifier: CrossSellingTabType) => {
        setTimeout(() => setActiveNavIdentifier(nextNavIdentifier), 100)
    }

    const renderTabButtons = () => (
        <Stack direction="row" justifyContent="space-between" sx={{ marginTop: "-4px" }}>
            <Tabs size="small" value={activeNavIdentifier} onChange={handleNavSelect} sx={{ "& .MuiTabs-indicator": { borderRadius: "3px" } }}>
                {displayCustomerAlsoBoughtTab && (
                    <Tab sx={{ borderRadius: "3px" }} value={ECrossSellingType.alsoBoughtArticles} label={translateText(13838)} />
                )}
                {!!data.recommendedProductGroups?.length && (
                    <Tab sx={{ borderRadius: "3px" }} value={ECrossSellingType.recommendedProductGroups} label={translateText(14014)} />
                )}
            </Tabs>
            <Tooltip title={translateText(70)}>
                <IconButton onClick={onClose} sx={{ padding: "0 0.5em" }}>
                    <Icon name="close" />
                </IconButton>
            </Tooltip>
        </Stack>
    )

    const renderCustomerAlsoBought = () => (
        <Fade in={activeNavIdentifier === ECrossSellingType.alsoBoughtArticles} timeout={100}>
            <Box>
                <ArticlesChain articleChain={displayArticlesChain} showArticleImages={showArticleImages} compactView={compactView} />
                <Stack>
                    <Typography ml={0.5} mb={1} variant="body2">
                        {translateText(13837)}
                    </Typography>
                    {displayCustomerAlsoBoughtButton && !!data.alsoBoughtProductGroups?.length && (
                        <Box>
                            {data.alsoBoughtProductGroups.map((group) => (
                                <ProductGroupButton
                                    key={group?.name}
                                    productGroup={group?.name}
                                    onProductGroupClick={() => onProductGroupClick?.(group, ECrossSellingType.alsoBoughtProductGroups)}
                                />
                            ))}
                        </Box>
                    )}
                    {!displayCustomerAlsoBoughtButton && data.alsoBoughtArticles?.length && (
                        <ArticleListFeaturesDisableProvider disableSelection>
                            <ParticularStateProvider value={pureListContext}>
                                <PureList listId={crossSellingListId} listType={crossSellingListId} />
                            </ParticularStateProvider>
                        </ArticleListFeaturesDisableProvider>
                    )}
                    <Icon
                        name="poweredByTM"
                        sx={{ width: "180px", height: "20px", alignSelf: "end", opacity: "0.5", marginTop: "-6px" }}
                        viewBox="0 0 180 20"
                    />
                </Stack>
            </Box>
        </Fade>
    )

    const renderRecommendedProductGroups = () => (
        <Fade in={activeNavIdentifier === ECrossSellingType.recommendedProductGroups} timeout={100}>
            <Box>
                <ArticlesChain articleChain={chains.recommendedProductGroups} showArticleImages={showArticleImages} compactView={compactView} />
                <Stack>
                    <Typography ml={0.5} mb={1} variant="body2">
                        {translateText(14015)}
                    </Typography>
                    {!!data.recommendedProductGroups?.length && (
                        <Box>
                            {data.recommendedProductGroups.map((group) => (
                                <ProductGroupButton
                                    key={group?.name}
                                    productGroup={group?.name}
                                    onProductGroupClick={() => onProductGroupClick?.(group, ECrossSellingType.recommendedProductGroups)}
                                />
                            ))}
                        </Box>
                    )}
                    <Icon
                        name="poweredByTM"
                        sx={{ width: "180px", height: "20px", alignSelf: "end", opacity: "0.5", marginTop: "-6px" }}
                        viewBox="0 0 180 20"
                    />
                </Stack>
            </Box>
        </Fade>
    )

    const orderStr = ((articleOrder ?? 0) + 1).toString()

    return (
        <Container sx={{ order: orderStr }}>
            {isLoading && <LinearProgress />}
            {renderTabButtons()}
            <TabPanel value={ECrossSellingType.alsoBoughtArticles} index={activeNavIdentifier}>
                {renderCustomerAlsoBought()}
            </TabPanel>
            <TabPanel value={ECrossSellingType.recommendedProductGroups} index={activeNavIdentifier}>
                {renderRecommendedProductGroups()}
            </TabPanel>
        </Container>
    )
})

export const CrossSelling = memo(({ listType, newSearchLoaded, crossSellingModalType }: CrossSellingProps) => {
    const enableCrossSelling = useArticleListConfiguration((x) => x.enableCrossSelling)

    const isAlternativeSearch = usePartsAlternativesModuleState((x) => !!x)
    const displayCrossSelling = isCrossSellingEnabledForList(listType, isAlternativeSearch, crossSellingModalType)

    if (!enableCrossSelling || !displayCrossSelling) {
        return null
    }

    return (
        <CrossSellingComponent
            listType={listType}
            newSearchLoaded={newSearchLoaded}
            isAlternativeSearch={isAlternativeSearch}
            crossSellingModalType={crossSellingModalType}
        />
    )
})
